import { CreditCard } from '@mui/icons-material';
import ApplePayBadge from '@on3/icons/components/ApplePayBadge';
import { Form } from '@on3/ui-lib/components/Form';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { Select } from '@on3/ui-lib/components/Select/Select';
import { useSubscribe } from '@on3/ui-lib/components/Subscription/Provider';
import { usePrevious } from '@on3/ui-lib/hooks/usePrevious';
import { isDiff } from '@on3/ui-lib/utils/compare';
import { countries } from '@on3/ui-lib/utils/recurly';
import {
  CardElement,
  type CardElementChangeEvent,
  useCheckoutPricing,
  useRecurly,
} from '@recurly/react-recurly';
import { type CheckoutPrice, type TokenPayload } from '@recurly/recurly-js';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { object, string } from 'yup';

import styles from './CardForm.module.scss';

declare global {
  interface Window {
    ApplePaySession?: any;
  }
}

const schema = object({
  firstName: string().required('First name is required'),
  lastName: string().required('Last name is required'),
  country: string().required('Country is required'),
  postalCode: string()
    .test('postalCodeRequired', 'Postal code is required', (value, ctx) => {
      const { country } = ctx.parent;
      const countryData = countries.find((c) => c.code === country);

      if (countryData?.postalCodeRequired) {
        return !!value;
      }

      return true;
    })
    .test('postalCodeFormat', 'Postal code format is invalid', (value, ctx) => {
      const { country } = ctx.parent;

      if (country === 'US') {
        return !!value?.match(/^\d{5}$/);
      }

      if (country === 'CA') {
        return !!value?.match(/^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/);
      }

      return true;
    }),
});

const initialValues = {
  firstName: '',
  lastName: '',
  country: 'US',
  postalCode: '',
};

const countryOptions = countries.map((c) => ({
  label: c.name,
  value: c.code,
  showPostal: c.postalCodeRequired,
}));

export const CardForm = ({
  submitText = 'Pay Now',
  disabled = false,
  planCode,
  setPricing,
  callback,
  children,
}: {
  submitText?: string;
  disabled?: boolean;
  planCode?: string;
  setPricing?: (price: CheckoutPrice | null) => void;
  callback?: (data: {
    token: TokenPayload;
    values: typeof initialValues;
  }) => Promise<void>;
  children?: React.ReactNode;
}) => {
  const { osSite } = useSubscribe(); // todo: remove this once apple pay is verified
  const formRef = useRef<HTMLFormElement>(null);
  const [values, setValues] = useState(initialValues);
  const [isValidCard, setIsValidCard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [paymentType, setPaymentType] = useState<'card' | 'applePay'>('card');

  const recurly = useRecurly();
  const [{ price }, setRecurlyPricing] = useCheckoutPricing({});
  const prevPrice = usePrevious(price);

  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);
  const applePay = useMemo(
    () =>
      isApplePayAvailable
        ? recurly.ApplePay({
            country: values.country,
            currency: price.currency.code,
            recurring: true,
            total: price.now.total,
          })
        : null,
    [isApplePayAvailable, recurly, values.country, price],
  );

  // Check for Apple Pay availability
  useEffect(() => {
    // Fairly dickinson
    if (window.ApplePaySession?.canMakePayments() && osSite?.key === 280) {
      setIsApplePayAvailable(true);
    } else {
      setIsApplePayAvailable(false);
    }
  }, [osSite]);

  // Update Recurly pricing
  useEffect(() => {
    setRecurlyPricing({
      subscriptions: planCode ? [{ plan: planCode }] : [],
      address: {
        first_name: values.firstName,
        last_name: values.lastName,
        country: values.country,
        postal_code: values.postalCode,
      },
    });
  }, [planCode, setRecurlyPricing, values]);

  useEffect(() => {
    if (JSON.stringify(price) === JSON.stringify(prevPrice)) return;

    setPricing?.(price || null);
  }, [prevPrice, price, setPricing]);

  const requirePostalCode = countryOptions.find(
    (c) => c.value === values.country,
  )?.showPostal;

  const postalCodePlaceholder = useMemo(() => {
    if (values.country === 'US') return '00000';
    if (values.country === 'CA') return 'A0A 0A0';

    return 'Postal Code';
  }, [values.country]);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const onCardChange = (e: CardElementChangeEvent) => {
    setIsValidCard(e.valid);
  };

  const onSubmit = useCallback(
    async (v: typeof initialValues) => {
      try {
        await new Promise((resolve, reject) => {
          if (!formRef.current) {
            reject(new Error('Form reference is null'));

            return;
          }

          recurly.token(formRef.current, async (err, token) => {
            if (err) {
              setError(
                'There was an error processing your card. Please try again.',
              );
              console.error('Recurly tokenization error:', err);
              reject(err);
            } else {
              await callback?.({ token, values: v });
              resolve(token);
            }
          });
        });
      } catch (e) {
        console.error('Recurly error:', e);
      }
    },
    [recurly, callback],
  );

  const handleApplePay = useCallback(async () => {
    if (!applePay) return;

    if (!price?.now?.total) {
      setError('Pricing information is not available.');

      return;
    }

    applePay.on('error', (err) => {
      setError('Apple Pay error: ' + err.message);
      console.error('Apple Pay error:', err);
    });

    applePay.on('token', async (token) => {
      try {
        await callback?.({ token, values });
      } catch (e) {
        setError('Error processing Apple Pay payment.');
        console.error('Apple Pay callback error:', e);
      }
    });

    applePay.begin();
  }, [price?.now?.total, applePay, callback, values]);

  return (
    <Form onSubmit={onSubmit} ref={formRef} schema={schema} values={values}>
      {({ errors, processing, validate }) => (
        <div
          className={clsx(styles.form, {
            [styles.processing]: processing,
          })}
        >
          {isApplePayAvailable && (
            <div className={styles.paymentTypes}>
              <button
                className={clsx(styles.cardPay, styles.paymentType, {
                  [styles.active]: paymentType === 'card',
                })}
                onClick={() => setPaymentType('card')}
                type="button"
              >
                <CreditCard />
                <span className={styles.label}>Card</span>
              </button>
              <button
                className={clsx(styles.applePay, styles.paymentType, {
                  [styles.active]: paymentType === 'applePay',
                })}
                onClick={() => setPaymentType('applePay')}
                type="button"
              >
                <ApplePayBadge />
                <span className={styles.label}>Apple Pay</span>
              </button>
            </div>
          )}

          {/* Card payment fields */}
          {paymentType === 'card' && (
            <>
              <div className={styles.group}>
                <Input
                  data-recurly="first_name"
                  error={errors.firstName}
                  label="First Name"
                  name="firstName"
                  onBlur={validate}
                  onChange={onChange}
                  required
                  value={values.firstName}
                />
                <Input
                  data-recurly="last_name"
                  error={errors.lastName}
                  label="Last Name"
                  name="lastName"
                  onBlur={validate}
                  onChange={onChange}
                  required
                  value={values.lastName}
                />
              </div>
              <div className={styles.cardElement}>
                <span className={styles.label}>Card details *</span>
                <CardElement onChange={onCardChange} />
              </div>
            </>
          )}

          <div className={styles.group}>
            <Select
              data-recurly="country"
              error={errors.country}
              label="Country"
              name="country"
              onBlur={validate}
              onChange={onChange}
              options={countryOptions}
              required
              value={values.country}
            />
            {requirePostalCode && (
              <Input
                data-recurly="postal_code"
                error={errors.postalCode}
                label={values.country !== 'US' ? 'Postal Code' : 'ZIP Code'}
                name="postalCode"
                onBlur={validate}
                onChange={onChange}
                placeholder={postalCodePlaceholder}
                required
              />
            )}
          </div>

          {error && <p className={styles.error}>{error}</p>}

          {/* Additional content */}
          {children}

          {/* Card payment submit button */}
          {paymentType === 'card' && (
            <button
              className={styles.submitBtn}
              disabled={
                processing ||
                disabled ||
                !isDiff(initialValues, values) ||
                !isValidCard
              }
              type="submit"
            >
              {processing ? 'Processing...' : submitText}
            </button>
          )}
          {paymentType === 'applePay' && (
            <button
              className={styles.applePayBtn}
              disabled={processing || disabled || !price}
              onClick={handleApplePay}
              type="button"
            >
              {processing ? 'Processing...' : 'Pay with Apple Pay'}
            </button>
          )}
        </div>
      )}
    </Form>
  );
};
